import { Box, Grid as MuiGrid } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { memo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { GridContainer } from '~/components/GridContainer';
import { Typography } from '~/components/Typography';
import { EmptyState } from '~/components/empty-state';
import CreateShowIllustration from '~/images/illustrations/create-show.svg';
import type {
  ShowList__Show as Show,
  ShowList__ShowFolder as ShowFolder,
} from '../../queries/queries.generated';
import { ShowGridItem } from './ShowGridItem';

export interface ShowGridProps {
  shows: readonly Show[];
  folders: readonly ShowFolder[];
  loading?: boolean;
  selectedShow?: Show;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  onSelectedShow: (show: Show | undefined) => void;
  selectedIds: GridRowSelectionModel;
}

export const ShowGrid = memo(
  ({
    shows,
    folders,
    loading,
    selectedShow,
    onCheck,
    onSelectedShow,
    selectedIds,
  }: ShowGridProps) => {
    const { t } = useTranslation();

    return (
      <GridContainer
        loading={loading}
        isEmpty={folders.length === 0 && shows.length === 0}
        emptyState={
          <EmptyState
            illustration={CreateShowIllustration}
            header={t('pages.shows.components.empty_state.header')}
            description={t('pages.shows.components.empty_state.description')}
          />
        }
      >
        {folders.length > 0 && (
          <Box
            sx={{
              paddingBottom: 4,
              marginBottom: 4,
              borderBottom: shows.length > 0 ? 1 : 0,
              borderBottomColor: 'borders.main',
            }}
          >
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {t('pages.shows.components.grid.folders_heading')} ({folders.length})
            </Typography>
            <MuiGrid container spacing={2}>
              {folders.map((folder) => (
                <ShowGridItem
                  key={`ShowFolder-${folder.id}`}
                  item={folder}
                  selectedShow={selectedShow}
                  onCheck={onCheck}
                  onSelectedShow={onSelectedShow}
                  selectedIds={selectedIds}
                />
              ))}
            </MuiGrid>
          </Box>
        )}
        {shows.length > 0 && (
          <Box sx={{ paddingBottom: 4 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {t('common.tabs.shows')} ({shows.length})
            </Typography>
            <MuiGrid container spacing={2}>
              {shows.map((show) => (
                <ShowGridItem
                  key={show.id}
                  item={show}
                  selectedShow={selectedShow}
                  onCheck={onCheck}
                  onSelectedShow={onSelectedShow}
                  selectedIds={selectedIds}
                />
              ))}
            </MuiGrid>
          </Box>
        )}
      </GridContainer>
    );
  },
);

ShowGrid.displayName = 'ShowGrid';
